import { Button, Col, Divider, Flex, Form, FormInstance, Input, Row } from 'antd';
import { FunctionComponent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';

// components
import FormElement from 'components/formElement/formElement';
import SelectElement from 'components/selectElement/selectElement';
import PhoneInputComponent, { PhoneInputReturn } from 'components/phoneInput/phoneInput';
import StatusComponent from 'components/status/status';
import CompoundElement from 'components/compoundElement/compoundElement';

// services
import { onCheckNationalId, onGetShareholderSharesLimit } from '../../commonServices';
import NotificationService from 'core/services/notification';
import { useSelector } from 'react-redux';
import { RootState } from 'stores/store';
import { onGetCountries } from 'core/services/countries';
import { isRTL } from 'core/services/language';
import { checkPhoneValidation } from 'utils/validators/phone';
import { onGenerateCompanyShareholder } from '../services/heirsService';
import FormNumberElement from 'components/formNumberElement/formNumberElement';
import { formatErrorMessages } from 'utils/errorMessage';
import { HeirTransfer } from '../models/heir';
import { ShareholderSharesLimit } from '../../commonModels';

const COMMON_SHARE_KEYS = ['free_common_shares_count', 'suspended_common_shares_count', 'mortgaged_common_shares_count'] as const;

/** Total common shares (free + suspended + mortgaged) assigned to one heir. */
export const heirCommonSharesTotal = (heir: any) => COMMON_SHARE_KEYS.reduce((total, key) => total + Number(heir?.[key] || 0), 0);

/** The heir's shares limit, kept on the heir's form value (not a registered field, so it's never submitted). */
export const heirSharesLimit = (heir: any): ShareholderSharesLimit | undefined => heir?.shares_limit;

interface HeirInputProps {
	name: number; // Form.List field name (index within the heirs list)
	form: FormInstance;
	deceased: HeirTransfer['deceased'];
}

const HeirInput: FunctionComponent<HeirInputProps> = ({ form, name, deceased }): JSX.Element => {
	const { t } = useTranslation();
	const availableShares = useSelector((state: RootState) => state.heir.availableShares);

	const heirPath = ['heirs', name];
	const newShareholderPath = [...heirPath, 'newShareholder'];

	// availableShares already excludes this heir's own input, so add it back to get this input's max
	const heirValues = Form.useWatch(heirPath, form);
	const maxShares = (key: string, available: number) => available + Number(heirValues?.[key] || 0);

	const nationalIdsToCompare = [
		deceased.shareholder.national_id,
		...((form.getFieldValue('heirs') || []) as any[])
			// .filter((_, index) => index !== name) // don't compare this input against its own heir entry
			.map((heir) => String(heir?.national_id ?? ''))
			.filter((v) => v !== ''),
	];

	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [isChecked, setIsChecked] = useState<boolean>(false);
	const [isGenerating, setIsGenerating] = useState<boolean>(false);
	const [isCountriesLoading, setIsCountriesLoading] = useState<boolean>(false);

	const [shareholderExists, setShareholderExists] = useState<boolean>(false);
	const [countries, setCountries] = useState<any[]>([]);
	const [nationalId, setNationalId] = useState<string>('');

	useEffect(() => {
		getCountriesHandler();
	}, []);

	const getCountriesHandler = async () => {
		try {
			setIsCountriesLoading(true);
			const countriesResponse = await onGetCountries();
			setCountries(
				countriesResponse.map((country) => ({
					label: isRTL() ? country.translated_name.ar : country.translated_name.en,
					value: country.id,
				})),
			);
			setIsCountriesLoading(false);
		} catch (error) {
			setIsCountriesLoading(false);
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.FETCH_ERROR'));
		}
	};

	const onChangePhone = ({ number, dialCode }: PhoneInputReturn) => {
		form.setFieldValue([...newShareholderPath, 'phone_number'], number);
		form.setFieldValue([...newShareholderPath, 'phone_country_code'], dialCode);
	};

	const validateCompanySharesLimit = () => {
		const heir = form.getFieldValue(heirPath);
		const companyRemaining = heirSharesLimit(heir)?.company_remaining_common_shares;

		if (companyRemaining != null && heirCommonSharesTotal(heir) > companyRemaining) {
			return Promise.reject(new Error(t('ACTIONS.COMPANY_SHARES_LIMIT_EXCEEDED', { count: companyRemaining.toLocaleString() as any })));
		}

		return Promise.resolve();
	};

	const validatePhone = (_: any, value: any) => {
		if (checkPhoneValidation(form.getFieldValue([...newShareholderPath, 'phone_country_code']), value)) {
			return Promise.resolve();
		} else if (value) return Promise.reject(new Error(t('COMMON.PHONE_INVALID')));
		else return Promise.reject();
	};

	const checkNationalId = async (nationalId: string) => {
		try {
			setIsLoading(true);

			const [response, sharesLimit] = await Promise.all([onCheckNationalId(nationalId), onGetShareholderSharesLimit(nationalId)]);

			form.setFieldValue([...heirPath, 'shares_limit'], sharesLimit);

			if (response?.data?.shareholder) {
				form.setFieldValue(heirPath, {
					...form.getFieldValue(heirPath),
					name: response.data.shareholder?.name || '',
					country: response.data.shareholder?.phone_country_code || '',
					date_of_birth: response.data.shareholder?.date_of_birth || '',
					national_id: response.data.shareholder?.national_id || '',
					phone: response.data.shareholder?.phone_number || '',
					shareholder_id: response.data.shareholder?.id || '',
					free_common_shares_count: 0,
					suspended_common_shares_count: 0,
					mortgaged_common_shares_count: 0,
					free_preferred_shares_count: 0,
					suspended_preferred_shares_count: 0,
					mortgaged_preferred_shares_count: 0,
				});
			}

			setShareholderExists(response.data.shareholder_exists);
			setIsChecked(true);
			setIsLoading(false);
		} catch (error: any) {
			setIsLoading(false);

			if (error?.status == 422) {
				NotificationService.error(t('COMMON.ERROR'), formatErrorMessages(error));
				return false;
			}

			NotificationService.error(t('COMMON.ERROR'), t('COMMON.FETCH_ERROR'));
		}
	};

	const generateShareholderHandler = async () => {
		try {
			await form.validateFields([
				[...newShareholderPath, 'name'],
				[...newShareholderPath, 'country_id'],
				[...newShareholderPath, 'phone_country_code'],
				[...newShareholderPath, 'phone_number'],
			]);

			setIsGenerating(true);

			const payload = {
				...form.getFieldValue(newShareholderPath),
				national_id: nationalId,
			};

			const response = await onGenerateCompanyShareholder(payload);

			if (response) {
				form.setFieldValue([...heirPath, 'name'], response.name);
				form.setFieldValue([...heirPath, 'shareholder_id'], response.id);

				setShareholderExists(true);
			}

			setIsGenerating(false);

			NotificationService.success(t('COMMON.SUCCESS'), t('ACTIONS.SHAREHOLDER_GENERATED_SUCCESSFULLY'));
		} catch (error: any) {
			if (error?.errorFields) return; // skip form validation errors

			setIsGenerating(false);
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.SUBMIT_ERROR'));
		}
	};

	const resetData = (value: string) => {
		form.resetFields([
			[...newShareholderPath, 'name'],
			[...newShareholderPath, 'country_id'],
			[...newShareholderPath, 'phone_country_code'],
			[...newShareholderPath, 'phone_number'],
		]);

		// clear the previously fetched shareholder data so its national_id doesn't linger in the form
		form.setFieldValue(heirPath, {
			...form.getFieldValue(heirPath),
			name: undefined,
			national_id: undefined,
			shareholder_id: undefined,
			shares_limit: undefined,
		});

		setNationalId(value);
		setIsChecked(false);
		setShareholderExists(false);
	};

	return (
		<>
			<CompoundElement
				buttonText={t('COMMON.CHECK')}
				onSubmit={checkNationalId}
				label={t('COMMON.NATIONAL_ID')}
				isLoading={isLoading}
				onChange={resetData}
				valueToCompare={nationalIdsToCompare}
			/>

			{isChecked && (
				<>
					<Divider />

					<motion.div initial={{ opacity: 0, transform: 'translateY(-10px)' }} animate={{ opacity: 1, transform: 'translateY(0px)' }}>
						<Row gutter={[16, 16]}>
							{shareholderExists ? (
								<>
									<Form.Item hidden name={[name, 'shareholder_id']}>
										<Input />
									</Form.Item>

									<Form.Item name={[name, 'national_id']}>
										<Input hidden />
									</Form.Item>

									<Col span={24} md={12}>
										<FormElement name={[name, 'name']} disabled label={t('ACTIONS.HEIR_NAME')} type="text" />
									</Col>
								</>
							) : (
								<>
									<Col span={24} md={12} lg={8}>
										<FormElement
											name={[name, 'newShareholder', 'name']}
											label={t('ACTIONS.HEIR_NAME')}
											type="text"
											rules={[{ required: true, message: t('COMMON.FIELD_REQUIRED') }]}
										/>
									</Col>

									<Col span={24} md={12} lg={8}>
										<SelectElement
											name={[name, 'newShareholder', 'country_id']}
											label={t('COMMON.COUNTRY')}
											options={countries}
											isLoading={isCountriesLoading}
											onChange={() => {}}
											rules={[{ required: true, message: t('COMMON.FIELD_REQUIRED') }]}
										/>
									</Col>

									<Col span={24} md={12} lg={8}>
										<Form.Item
											name={[name, 'newShareholder', 'phone_number']}
											rules={[{ required: true, message: t('COMMON.PHONE_REQUIRED') }, { validator: validatePhone }]}>
											<PhoneInputComponent onChange={onChangePhone} />
										</Form.Item>
										<Form.Item name={[name, 'newShareholder', 'phone_country_code']} hidden>
											<Input hidden />
										</Form.Item>
									</Col>

									<Col span={24}>
										<Flex justify="flex-end" className="mt-3">
											<Button type="primary" size="middle" htmlType="button" loading={isGenerating} onClick={generateShareholderHandler}>
												{t('COMMON.GENERATE')}
											</Button>
										</Flex>
									</Col>
								</>
							)}
						</Row>

						{shareholderExists && (
							<Row className="mt-5" gutter={[16, 16]}>
								<>
									<Col span={24} md={8}>
										<FormNumberElement
											name={[name, 'free_common_shares_count']}
											label={t('ACTIONS.FREE_COMMON_SHARES_COUNT')}
											min={0}
											max={maxShares('free_common_shares_count', availableShares.free_common)}
											zeroOnBlur
											addonAfter={
												<StatusComponent
													style={{ paddingBlock: 3 }}
													bordered
													color="green"
													small
													text={`${t('COMMON.AVAILABLE')}: ${availableShares.free_common.toLocaleString()}`}
												/>
											}
										/>
									</Col>

									<Col span={24} md={8}>
										<FormNumberElement
											name={[name, 'suspended_common_shares_count']}
											label={t('ACTIONS.SUSPENDED_COMMON_SHARES_COUNT')}
											min={0}
											max={maxShares('suspended_common_shares_count', availableShares.suspended_common)}
											zeroOnBlur
											addonAfter={
												<StatusComponent
													style={{ paddingBlock: 3 }}
													bordered
													color="green"
													small
													text={`${t('COMMON.AVAILABLE')}: ${availableShares.suspended_common}`}
												/>
											}
										/>
									</Col>

									<Col span={24} md={8}>
										<FormNumberElement
											name={[name, 'mortgaged_common_shares_count']}
											label={t('ACTIONS.MORTGAGED_COMMON_SHARES_COUNT')}
											min={0}
											max={maxShares('mortgaged_common_shares_count', availableShares.mortgaged_common)}
											zeroOnBlur
											addonAfter={
												<StatusComponent
													style={{ paddingBlock: 3 }}
													bordered
													color="green"
													small
													text={`${t('COMMON.AVAILABLE')}: ${availableShares.mortgaged_common}`}
												/>
											}
										/>
									</Col>

									<Col span={24}>
										<Form.Item
											name={[name, 'common_shares_limit']}
											className="error-only"
											dependencies={COMMON_SHARE_KEYS.map((key) => [...heirPath, key])}
											rules={[{ validator: validateCompanySharesLimit }]}>
											<Input hidden />
										</Form.Item>
									</Col>
								</>

								{deceased.preferred_shares_count && deceased.preferred_shares_count > 0 ? (
									<>
										<Col span={24} md={8}>
											<FormNumberElement
												name={[name, 'free_preferred_shares_count']}
												label={t('ACTIONS.FREE_PREFERRED_SHARES_COUNT')}
												min={0}
												max={maxShares('free_preferred_shares_count', availableShares.free_preferred)}
												zeroOnBlur
												addonAfter={
													<StatusComponent
														style={{ paddingBlock: 3 }}
														bordered
														color="green"
														small
														text={`${t('COMMON.AVAILABLE')}: ${availableShares.free_preferred}`}
													/>
												}
											/>
										</Col>

										<Col span={24} md={8}>
											<FormNumberElement
												name={[name, 'suspended_preferred_shares_count']}
												label={t('ACTIONS.SUSPENDED_PREFERRED_SHARES_COUNT')}
												min={0}
												max={maxShares('suspended_preferred_shares_count', availableShares.suspended_preferred)}
												zeroOnBlur
												addonAfter={
													<StatusComponent
														style={{ paddingBlock: 3 }}
														bordered
														color="green"
														small
														text={`${t('COMMON.AVAILABLE')}: ${availableShares.suspended_preferred}`}
													/>
												}
											/>
										</Col>

										<Col span={24} md={8}>
											<FormNumberElement
												name={[name, 'mortgaged_preferred_shares_count']}
												label={t('ACTIONS.MORTGAGED_PREFERRED_SHARES_COUNT')}
												min={0}
												max={maxShares('mortgaged_preferred_shares_count', availableShares.mortgaged_preferred)}
												zeroOnBlur
												addonAfter={
													<StatusComponent
														style={{ paddingBlock: 3 }}
														bordered
														color="green"
														small
														text={`${t('COMMON.AVAILABLE')}: ${availableShares.mortgaged_preferred}`}
													/>
												}
											/>
										</Col>
									</>
								) : null}
							</Row>
						)}
					</motion.div>
				</>
			)}
		</>
	);
};

export default HeirInput;
